import { useState } from 'react';
import { CircleUser as UserCircle, Mail, Shield } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { getRoleConfig } from '../../utils/helpers';
import type { User } from '../../types';
import Avatar from '../common/Avatar';
import Badge from '../common/Badge';
import UserDetailModal from './UserDetailModal';

export default function UserList() {
  const { state, getTeamById } = useApp();
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  return (
    <div className="p-4 md:p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-brand-50 flex items-center justify-center">
          <UserCircle size={20} className="text-brand-500" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-slate-800">Usuários</h2>
          <p className="text-sm text-slate-500">
            {state.users.length} membros ·{' '}
            {state.users.filter((u) => u.isOnline).length} online
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {state.users.map((user, index) => {
          const role = getRoleConfig(user.role);
          const userTeams = user.teamIds.map((id) => getTeamById(id)).filter(Boolean);
          const userTasks = state.tasks.filter((t) => t.assigneeIds.includes(user.id));
          const activeTasks = userTasks.filter(
            (t) => t.status === 'in_progress' || t.status === 'in_review'
          ).length;

          return (
            <div
              key={user.id}
              onClick={() => setSelectedUser(user)}
              className="bg-white rounded-2xl border border-slate-200 p-5 hover:shadow-lg hover:border-brand-200 transition-all duration-300 animate-slide-in-up cursor-pointer"
              style={{ animationDelay: `${index * 60}ms` }}
            >
              <div className="flex items-start gap-3 mb-4">
                <Avatar
                  name={user.name}
                  color={user.avatarColor}
                  size="xl"
                  isOnline={user.isOnline}
                />
                <div className="flex-1 min-w-0">
                  <h3 className="text-base font-semibold text-slate-800 truncate">
                    {user.name}
                  </h3>
                  <div className="flex items-center gap-1.5 text-xs text-slate-400 mt-0.5">
                    <Mail size={12} />
                    <span className="truncate">{user.email}</span>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2 mb-3">
                <div className="flex items-center gap-1">
                  <Shield size={12} className="text-slate-400" />
                  <Badge className={role.color}>{role.label}</Badge>
                </div>
              </div>

              <div className="flex flex-wrap gap-1.5 mb-3">
                {userTeams.map((team) => (
                  <span
                    key={team!.id}
                    className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-medium border"
                    style={{
                      borderColor: `${team!.color}40`,
                      backgroundColor: `${team!.color}10`,
                      color: team!.color,
                    }}
                  >
                    {team!.name}
                  </span>
                ))}
              </div>

              <div className="flex items-center justify-between pt-3 border-t border-slate-100 text-xs">
                <span className="text-slate-500">
                  {userTasks.length} tarefas totais
                </span>
                <span className="text-brand-600 font-medium">
                  {activeTasks} ativas
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {selectedUser && (
        <UserDetailModal user={selectedUser} onClose={() => setSelectedUser(null)} />
      )}
    </div>
  );
}
